import { useCallback, useEffect, useMemo, type ChangeEvent, type RefObject } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import Modal from 'LuxKit/Modal/Modal'
import Checkbox from 'LuxKit/Checkbox/CheckboxInput'
import RadioInput from 'LuxKit/Radio/RadioInput'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { useMultiValueToggle } from 'hooks/useToggle'

const Options = styled(Group)`
  min-width: ${rem(240)};
`

interface Option {
  value: string
  label: string
}

export interface HotelListingFilterPanelProps {
  label: string
  options: Array<Option>
  selectedValues: Array<string>
  onApply: (values: Array<string>) => void
  single?: boolean
  isMobile: boolean
  open: boolean
  onClose: () => void
  triggerRef: RefObject<HTMLButtonElement | null>
}

function HotelListingFilterPanel({ label, options, selectedValues, onApply, single = false, isMobile, open, onClose, triggerRef }: HotelListingFilterPanelProps) {
  const intl = useIntl()
  const { arrayValues: pendingValues, set: setPendingValues, setCopy: pendingSelection, on: select, off: deselect } = useMultiValueToggle(selectedValues)
  useEffect(() => {
    if (open) setPendingValues(selectedValues)
  }, [open, selectedValues, setPendingValues])
  useEffect(() => {
    if (!open) return
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      event.stopImmediatePropagation()
      onClose()
      triggerRef.current?.focus()
    }
    window.addEventListener('keydown', handleEscape, true)
    return () => window.removeEventListener('keydown', handleEscape, true)
  }, [onClose, open, triggerRef])
  const change = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target
    if (single) setPendingValues([value])
    else if (checked) select(value)
    else deselect(value)
  }, [deselect, select, setPendingValues, single])
  const apply = useCallback(() => {
    onApply(pendingValues)
    onClose()
  }, [onApply, onClose, pendingValues])
  const reset = useCallback(() => setPendingValues([]), [setPendingValues])
  const applyLabel = intl.formatMessage({
    id: 'ENxPPE_4gjNvtXZpYAUq',
    defaultMessage: 'Apply',
    description: 'Apply button text for search form panels in the search interface',
  })
  const resetLabel = intl.formatMessage({
    id: 'W40qxOf1DBv_Bty5ZbvD',
    defaultMessage: 'Reset',
    description: 'Button to clear the pending selection in a Trip Planner hotel filter dropdown.',
  })
  const primaryActionProps = useMemo(() => ({ children: applyLabel, onClick: apply }), [apply, applyLabel])
  const secondaryActionProps = useMemo(() => ({ children: resetLabel, onClick: reset, kind: 'tertiary' as const }), [reset, resetLabel])
  const content = (
    <Options direction="vertical" gap={16}>
      {!options.length && <BodyText variant="medium">{intl.formatMessage({
        id: 'AHhJosT1xTNhcI2Ns5pp',
        defaultMessage: 'No available filters',
        description: 'Message in a Trip Planner hotel filter dropdown when the search has no filter options.',
      })}
      </BodyText>}
      {single && options.map(option => (
        <RadioInput key={option.value} name={label} value={option.value} checked={pendingSelection.has(option.value) || (!pendingValues.length && option.value === '0')} onChange={change}>
          {option.label}
        </RadioInput>
      ))}
      {!single && options.map(option => (
        <Checkbox key={option.value} value={option.value} checked={pendingSelection.has(option.value)} onChange={change}>
          {option.label}
        </Checkbox>
      ))}
    </Options>
  )
  if (isMobile) {
    return (
      <Modal title={label} onClose={onClose} primaryActionText={applyLabel} onPrimaryActionClick={apply} secondaryActionText={resetLabel} onSecondaryActionClick={reset}>
        {content}
      </Modal>
    )
  }
  return (
    <DropdownSheet triggerRef={triggerRef} anchorRef={triggerRef} open={open} onClose={onClose} size="content" placement="bottom-start" portaled autoHeight primaryActionProps={primaryActionProps} secondaryActionProps={secondaryActionProps}>
      {content}
    </DropdownSheet>
  )
}

export default HotelListingFilterPanel
